import { pageTitle } from '@palaccount/config';
import { ArrowLeft, FileSpreadsheet, Loader2, Plus } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { Ltr } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Typeahead } from '@/components/ui/typeahead';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { downloadListXlsx, fetchAllPages, LIST_EXPORT_ROW_CAP } from '@/features/reports/export';
import { usePagedState, usePageRange } from '@/lib/paging';
import { useDebounced } from '@/lib/typeahead';
import { useMediaQuery } from '@/lib/use-viewport';
import { cn } from '@/lib/utils';

import { DocumentCard } from './document-card';
import { DocumentTable } from './document-table';
import { KpiStrip } from './kpi-tile';
import { LifecycleStepper } from './lifecycle-stepper';
import { LinkedDocChip } from './linked-doc-chip';
import { ListEmptyState } from './list-empty-state';
import { ListSearch } from './list-search';
import { ProgressLine } from './progress-line';
import { TotalsFooter } from './totals-footer';
import { readStoredView, ViewToggle, writeStoredView, type ListView } from './view-toggle';
import type { DocumentListConfig } from './types';

/**
 * THE SHARED LIST-AND-DETAIL SCREEN (wave 3A).
 *
 * SAL-02 was the one hand-built KPI + list/detail page; this is that page
 * with the Sales Orders taken out of it, driven by a `DocumentListConfig`.
 * What it adds over the original, for every page that configures it:
 *
 *  - THE SELECTION IS IN THE URL (`?selected=<id>`, beside `?page=`), so a
 *    link to one document's flow is shareable and survives a reload. A
 *    selected id that is not on the current page is read by `useById`.
 *  - A SEARCH BOX when the contract accepts `q` (GEN-25), debounced.
 *  - SPLIT ↔ TABLE: the same rows as a full-width table with checkbox
 *    selection and a bulk-actions slot; the choice is remembered per page.
 *  - EXPORT: every page of the current filter as a real .xlsx (NFR-32),
 *    walked at the contract's ceiling and capped (`LIST_EXPORT_ROW_CAP`).
 *  - NARROW SCREENS: the list first; a tap opens the detail full-width with
 *    a back control. Below `lg` there is no room for both.
 *
 * `useMediaQuery` rather than `lg:` classes, for `use-viewport.ts`'s reason:
 * the two shapes are two different DOMs and both are ordinary assertions.
 */
export const SPLIT_QUERY = '(min-width: 1024px)';

const useNothing = () => ({ data: undefined });

export function DocumentListPage<Row extends { id: string }, Status extends string, Stats = unknown>({
  config,
}: {
  config: DocumentListConfig<Row, Status, Stats>;
}) {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<Status | ''>('');
  const [q, setQ] = useState('');
  const search = useDebounced(q);
  // Wave 3B — the date window and the partner, both server predicates.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const paging = usePagedState();
  const wide = useMediaQuery(SPLIT_QUERY);
  const storageKey = config.storageKey ?? config.titleKey;
  const [view, setView] = useState<ListView>(() => readStoredView(storageKey));
  const changeView = (next: ListView) => {
    setView(next);
    writeStoredView(storageKey, next);
  };

  // ── the selection, in the URL ─────────────────────────────────────────
  const selectedParam = config.selectedParam ?? 'selected';
  const [params, setParams] = useSearchParams();
  const selectedId = params.get(selectedParam);
  const select = useCallback(
    (id: string | null) => {
      setParams(
        (current) => {
          const updated = new URLSearchParams(current);
          if (id === null) updated.delete(selectedParam);
          else updated.set(selectedParam, id);
          return updated;
        },
        { replace: true },
      );
    },
    [setParams, selectedParam],
  );

  useEffect(() => {
    document.title = pageTitle(t(config.titleKey));
  }, [t, config.titleKey]);

  // ── the reads ─────────────────────────────────────────────────────────
  const useList = config.useList;
  const filters = {
    status,
    q: search.trim(),
    dateFrom: config.dateRange ? dateFrom : '',
    dateTo: config.dateRange ? dateTo : '',
    partnerId: config.partner ? partnerId : '',
  };
  const { data, isPending, isError } = useList(filters, paging.page, paging.pageSize);
  const rows = data?.rows ?? [];
  const range = usePageRange(data, paging.pageSize);
  const onPage = rows.find((row) => row.id === selectedId) ?? null;
  // Off this page (a deep link, or the page moved): read it by id.
  const useById = config.useById ?? useNothing;
  const offPage = useById(selectedId !== null && onPage === null ? selectedId : null);
  const selected: Row | null = onPage ?? (offPage.data as Row | undefined) ?? null;

  // ── export ────────────────────────────────────────────────────────────
  const [exporting, setExporting] = useState(false);
  const [exportNote, setExportNote] = useState<string | null>(null);
  const exportConfig = config.export;
  const onExport = async () => {
    if (exportConfig === undefined) return;
    setExporting(true);
    setExportNote(null);
    try {
      const result = await fetchAllPages((page, pageSize) => exportConfig.fetchPage(filters, page, pageSize));
      downloadListXlsx(exportConfig.filename, exportConfig.columns, result.rows, i18n.dir() === 'rtl');
      setExportNote(
        result.capped
          ? t('doclist.exportCapped', { cap: LIST_EXPORT_ROW_CAP })
          : t('doclist.exportSummary', { rows: result.rows.length, total: result.total }),
      );
    } catch {
      setExportNote(t('common.loadFailed'));
    } finally {
      setExporting(false);
    }
  };

  const newButton = config.newAction ? (
    <Gated permission={config.newAction.permission} reasonKey={config.newAction.reasonKey}>
      <Button asChild>
        <Link to={config.newAction.to}>
          <Plus aria-hidden /> {t(config.newAction.labelKey)}
        </Link>
      </Button>
    </Gated>
  ) : null;

  const filterControls = (
    <div className="grid gap-2">
      {config.search ? (
        <ListSearch
          label={t(config.search.labelKey)}
          placeholder={t(config.search.placeholderKey)}
          value={q}
          onChange={paging.change(setQ)}
        />
      ) : null}
      <NativeSelect
        aria-label={t('doclist.filterStatus')}
        value={status}
        onChange={(event) => paging.change(setStatus)(event.target.value as Status | '')}
        className="h-10 w-full text-sm"
      >
        <option value="">{t('doclist.filterStatusAll')}</option>
        {config.statuses.map((value) => (
          <option key={value} value={value}>
            {config.statusLabel(value)}
          </option>
        ))}
      </NativeSelect>
      {config.partner ? (
        <PartnerFilter kind={config.partner.kind} value={partnerId} onChange={paging.change(setPartnerId)} />
      ) : null}
      {config.dateRange ? (
        /* The window on the document's own date — ISO in the box (dir=ltr, an LTR island), server-side bounds. */
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="date"
            dir="ltr"
            aria-label={t('doclist.dateFrom')}
            value={dateFrom}
            onChange={(event) => paging.change(setDateFrom)(event.target.value)}
            className="h-10 text-sm"
          />
          <Input
            type="date"
            dir="ltr"
            aria-label={t('doclist.dateTo')}
            value={dateTo}
            onChange={(event) => paging.change(setDateTo)(event.target.value)}
            className="h-10 text-sm"
          />
        </div>
      ) : null}
    </div>
  );

  const pager =
    !isPending && !isError && (data?.total ?? 0) > 0 ? (
      <Pager
        page={paging.page}
        pages={range.pages}
        first={range.first}
        last={range.last}
        total={range.total}
        onPage={paging.setPage}
        label={t(config.titleKey)}
      />
    ) : null;

  /** The list's three states, then the rows — as cards or as the table. */
  const body = (mode: ListView): ReactNode => {
    if (isPending) return <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />;
    if (isError) {
      return (
        <p role="alert" className="py-8 text-center text-sm text-destructive">
          {t('common.loadFailed')}
        </p>
      );
    }
    if (rows.length === 0) return <ListEmptyState message={t(config.emptyKey)} />;
    if (mode === 'table') {
      return (
        <DocumentTable
          rows={rows}
          card={config.card}
          labels={config.columns}
          selectedId={selectedId}
          onSelect={select}
          bulkActions={config.bulkActions}
        />
      );
    }
    return (
      <ul className="grid gap-1" aria-label={t(config.titleKey)}>
        {rows.map((row) => (
          <li key={row.id}>
            <DocumentCard model={config.card(row)} selected={row.id === selectedId} onSelect={() => select(row.id)} />
          </li>
        ))}
      </ul>
    );
  };

  const detail = (
    <Card>
      <CardContent className="grid gap-4">
        {!wide && selected ? (
          <div>
            <Button type="button" variant="outline" size="sm" onClick={() => select(null)}>
              <ArrowLeft aria-hidden className="rtl:rotate-180" /> {t('doclist.back')}
            </Button>
          </div>
        ) : null}
        {selected ? (
          <DetailPanel config={config} row={selected} />
        ) : (
          <p className="py-16 text-center text-sm text-muted-foreground">{t(config.selectOneKey)}</p>
        )}
      </CardContent>
    </Card>
  );

  // Narrow: the list, or the one document — never both.
  const narrowDetail = !wide && selected !== null;

  return (
    <div className="grid gap-4">
      {config.tabs}
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t(config.titleKey)}</h2>
        {wide ? <ViewToggle view={view} onChange={changeView} /> : null}
        {exportConfig ? (
          <Button type="button" variant="outline" disabled={exporting} onClick={() => void onExport()}>
            {exporting ? <Loader2 className="animate-spin" aria-hidden /> : <FileSpreadsheet aria-hidden />}
            {t('doclist.export')}
          </Button>
        ) : null}
        {newButton}
      </div>

      {config.kpis && !narrowDetail ? <KpiStrip kpis={config.kpis} /> : null}

      {exportNote ? (
        <p role="status" className="text-sm text-muted-foreground">
          {exportNote}
        </p>
      ) : null}
      {config.notice}

      {narrowDetail ? (
        detail
      ) : wide && view === 'table' ? (
        <div className="grid gap-4">
          <Card>
            <CardContent className="grid gap-3">
              <div className="grid gap-2 sm:grid-cols-[minmax(200px,320px)_auto] sm:items-start">{filterControls}</div>
              {body('table')}
              {pager}
            </CardContent>
          </Card>
          {selected ? detail : null}
        </div>
      ) : (
        <div className={cn('grid gap-4', wide && 'lg:grid-cols-[minmax(280px,380px)_1fr]')}>
          <Card>
            <CardContent className="grid gap-3">
              {filterControls}
              {body('split')}
              {pager}
            </CardContent>
          </Card>
          {wide ? detail : null}
        </div>
      )}
    </div>
  );
}

/**
 * Wave 3B — the partner box, its own component so the picker's search
 * (GEN-50, one panel of matches) is only ever asked for on a page that
 * draws the box: a list without a partner filter makes no partner read.
 */
function PartnerFilter({
  kind,
  value,
  onChange,
}: {
  kind: 'customer' | 'supplier';
  value: string;
  onChange: (id: string) => void;
}) {
  const { t } = useTranslation();
  const picker = usePartnerPicker(value || null, { type: kind });
  return (
    <Typeahead
      label={t(kind === 'customer' ? 'doclist.filterCustomer' : 'doclist.filterSupplier')}
      placeholder={t('doclist.filterPartnerPh')}
      value={value || null}
      onChange={(id) => onChange(id ?? '')}
      options={picker.options}
      selected={picker.optionFor(value || null)}
      query={picker.query}
      onQueryChange={picker.setQuery}
      loading={picker.loading}
      emptyLabel={t(kind === 'customer' ? 'picker.noMatchingCustomer' : 'picker.noMatchingSupplier')}
      clearLabel={t('doclist.filterPartnerAll')}
    />
  );
}

/** The selected document: header, links, flow, actions, lines, totals. */
function DetailPanel<Row extends { id: string }, Status extends string, Stats>({
  config,
  row,
}: {
  config: DocumentListConfig<Row, Status, Stats>;
  row: Row;
}) {
  const { t } = useTranslation();
  const { detail } = config;
  const code = config.card(row).code;
  const links = detail.links?.(row) ?? [];
  const steps = detail.steps?.of(row);
  const progress = detail.progress?.(row) ?? null;
  const lines = detail.lines;
  const totals = detail.totals?.(row) ?? null;
  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-mono text-lg font-bold">
          <Ltr value={code} />
        </span>
        {detail.chip(row)}
        {links.map((doc) => (
          <LinkedDocChip key={doc.key} doc={doc} />
        ))}
      </div>
      {detail.render ? (
        detail.render(row)
      ) : (
        <>
          {detail.subline ? <p className="text-sm text-muted-foreground">{detail.subline(row)}</p> : null}
          {steps && detail.steps ? <LifecycleStepper steps={steps} label={t(detail.steps.labelKey)} /> : null}
          {progress !== null ? <ProgressLine text={progress} /> : null}
          {detail.actions ? <div className="flex flex-wrap gap-2">{detail.actions(row)}</div> : null}
          {lines ? (
            <Table>
              <TableHeader>
                <TableRow>
                  {lines.columns.map((column) => (
                    <TableHead
                      key={column.key}
                      className={cn(column.align === 'end' && 'text-end', column.className)}
                    >
                      {t(column.labelKey)}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.rows(row).map((line) => (
                  <TableRow key={line.key}>
                    {line.cells.map((cell, index) => (
                      <TableCell
                        key={lines.columns[index]?.key ?? index}
                        className={cn(lines.columns[index]?.align === 'end' && 'text-end')}
                      >
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
              {totals ? (
                <TotalsFooter label={t(totals.labelKey)} totals={totals} colSpan={lines.columns.length} />
              ) : null}
            </Table>
          ) : null}
        </>
      )}
    </>
  );
}
